import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { api, ApiError, type User } from '@/api/client';
import { App } from '@/App';
import { ChoosePassword } from '@/components/ChoosePassword';
import { Login } from '@/components/Login';
import '@/styles/app.css';

const client = new QueryClient({
    defaultOptions: {
        queries: { retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2, refetchOnWindowFocus: false },
    },
});

// Sign-in, then one's own password if someone else set it (D-29), then the app.
function Root(): ReactNode {
    const queryClient = useQueryClient();
    const me = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
    const signedIn = (user: User): void => {
        queryClient.setQueryData(['me'], { user });
    };
    // Who is signed in goes first, on the query this component watches (clearing the cache first would orphan it and
    // leave the screen as it was); then whatever they had read.
    const signOut = (): void => {
        void api.logout().finally(() => {
            queryClient.setQueryData(['me'], null);
            queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
        });
    };
    if (me.isLoading) {
        return null;
    }
    if (!me.data) {
        return <Login onSignedIn={signedIn} />;
    }
    if (me.data.user.mustChangePassword) {
        return <ChoosePassword user={me.data.user} onChosen={signedIn} onSignOut={signOut} />;
    }

    return <App user={me.data.user} onLogout={signOut} />;
}

createRoot(document.getElementById('root') as HTMLElement).render(
    <StrictMode>
        <QueryClientProvider client={client}>
            <Root />
        </QueryClientProvider>
    </StrictMode>,
);
